import React, { useEffect, useMemo, useState } from "react";
import type { KbDocumentRequest, KbDocumentResponse } from "../types/KbDocumentConfig";

type Props = {
  value?: KbDocumentResponse | null; // 수정이면 값 주입
  onSubmit: (data: KbDocumentRequest, file?: File | null) => void | Promise<void>;
  onCancel: () => void;

  // 옵션: 필요하면 문서 타입/상태 목록을 props로 받을 수도 있음
  // docTypeOptions?: { value: string; label: string }[];
  // docStatusOptions?: { value: string; label: string }[];
};

const DEFAULT_FORM: KbDocumentRequest = {
  kbSourceId: 0,
  title: "",
  docType: "FILE",      // 프로젝트 기본값에 맞게 조정
  docStatus: "DRAFT",   // 기본값
  category: "",
  sourceUrl: "",
  tagsJson: "[]",
  summaryPrompt: "",
  useTf: "Y",
  delTf: "N",
  // version 등 서버에서 관리하는 값이 있다면 Request에서 제외하는 게 보통입니다.
};

function formatBytes(bytes?: number) {
  if (!bytes || bytes <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

type ProgressStep = 0 | 1 | 2 | 3 | 99;
// 0: idle, 1: 업로드/attach 중, 2: 해독/요약 생성 중, 3: 완료, 99: 실패

function stepLabel(step: ProgressStep) {
  switch (step) {
    case 1:
      return "1. 벡터스토어에 문서 업로드중...";
    case 2:
      return "2. 문서 해독해서 답변(요약) 생성 중...";
    case 3:
      return "3. 완료! 아래에서 답변(요약)을 확인하세요.";
    case 99:
      return "처리 실패";
    default:
      return "";
  }
}

export default function KbDocumentEditorForm({ value, onSubmit, onCancel }: Props) {
  const isEdit = !!value?.id;

  const [form, setForm] = useState<KbDocumentRequest>(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // 파일 상태
  const [file, setFile] = useState<File | null>(null);

  // 진행 상태(폴링 기반)
  const [progressStep, setProgressStep] = useState<ProgressStep>(0);
  const [progressText, setProgressText] = useState<string>("");
  const [liveDetail, setLiveDetail] = useState<KbDocumentResponse | null>(null);
  const [lockedAfterDone, setLockedAfterDone] = useState(false);

  const detail = liveDetail ?? value ?? null;

  const canSubmit = useMemo(() => {
    if (!form.kbSourceId) return false;
    if (!form.title?.trim()) return false;
    return true;
  }, [form.kbSourceId, form.title]);

  const isProcessing = saving;

  // 수정일 때 기존 파일 정보 표시용
  const existingFileInfo = useMemo(() => {
    const v = detail;   // value -> detail로 변경
    if (!v) return null;
    if (!v.originalFileName && !v.filePath) return null;
    return {
      name: v.originalFileName ?? "(파일명 없음)",
      size: v.fileSize ? formatBytes(v.fileSize) : "",
      path: v.filePath ?? "",
    };
  }, [detail]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
  
    if (f) {
      setLockedAfterDone(false); // 다시 저장 가능
      setProgressStep(0);
      setProgressText("");
    }
  };

  // value 변경 시 form 초기화
  useEffect(() => {
    setLockedAfterDone(false);   // 문서 바뀌면 잠금 초기화

    if (value) {
      setForm({
        kbSourceId: value.kbSourceId,
        title: value.title ?? "",
        docType: value.docType ?? "FILE",
        docStatus: value.docStatus ?? "DRAFT",
        category: value.category ?? "",
        sourceUrl: value.sourceUrl ?? "",
        tagsJson: value.tagsJson ?? "[]",
        summaryPrompt: value.summaryPrompt ?? "",
        useTf: value.useTf ?? "Y",
        delTf: value.delTf ?? "N",
      });
      setFile(null);
      setLiveDetail(value); // value를 liveDetail로 시작
    } else {
      setForm(DEFAULT_FORM);
      setFile(null);
      setLiveDetail(null);
    }
    setErr(null);
    setProgressStep(0);
    setProgressText("");
  }, [value]);

  useEffect(() => {
    if (!value) return;
    const step = computeStepFromDetail(value);
    if (step === 3) setLockedAfterDone(true); // 기존에 이미 완료된 문서면 잠금
  }, [value]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]:
        name === "kbSourceId"
          ? Number(value)
          : value,
    }));
  };

  const handleRemoveSelectedFile = () => {
    setFile(null);
  };

  // 단계 추정 로직 (DB 필드 변화 기반)
  const computeStepFromDetail = (d: KbDocumentResponse | null): ProgressStep => {
    if (!d) return 0;
  
    const status = (d.docStatus ?? "").toUpperCase();
  
    // 인덱싱 진행은 INDEXING일 때만
    if (status !== "INDEXING") {
      // 완료 상태면 3
      if (status === "INDEXED" && (d.indexSummary?.trim() || d.indexedAt)) return 3;
      if (status === "FAILED" && d.indexError?.trim()) return 99;
      return 0;
    }
  
    // INDEXING 상태일 때만 진행 단계
    if (d.indexError?.trim()) return 99;
    if (d.indexSummary?.trim() || d.indexedAt) return 3;
    if (d.vectorFileId?.trim()) return 2;
    return 1;
  };

  // 편의: edit 모드에서 현재 상세 상태만 계산한다. 상태 추적은 목록에서 수행한다.
  useEffect(() => {
    if (!detail?.id) return;

    const step = computeStepFromDetail(detail);
    setProgressStep(step);
    setProgressText(stepLabel(step));
  }, [detail]);

  useEffect(() => {
    if (progressStep === 3 && detail) {
      setForm((prev) => ({
        ...prev,
        tagsJson: detail.tagsJson?.trim() ? detail.tagsJson : "[]",
      }));
    }
  }, [progressStep, detail]);

  useEffect(() => {
    if (progressStep === 3) {
      setLockedAfterDone(true);
      setFile(null); // 완료되면 선택 파일 제거(안전장치)
    }
  }, [progressStep]);

  const ALLOWED_IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp", ".gif", ".bmp"];

  const getFileExtension = (fileName: string) => {
    const lastDotIndex = fileName.lastIndexOf(".");
    if (lastDotIndex === -1) return "";
    return fileName.slice(lastDotIndex).toLowerCase();
  };

  const handleSubmit = async () => {
    setErr(null);

    if (!canSubmit) {
      setErr("KB Source / 제목은 필수입니다.");
      return;
    }

    if (form.docType === "IMAGE") {
      if (!file) {
        setErr("IMAGE 타입은 이미지 파일 업로드가 필요합니다.");
        return;
      }

      const ext = getFileExtension(file.name);
      if (!ALLOWED_IMAGE_EXTENSIONS.includes(ext)) {
        setErr("허용되지 않은 이미지 확장자입니다. png, jpg, jpeg, webp, gif, bmp 파일만 업로드 가능합니다.");
        return;
      }
    }

    try {
      setSaving(true);
      await onSubmit(form, file);
    } catch (e: any) {
      setErr(e?.message ?? "저장 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const summaryText = detail?.indexSummary ?? "";
  const tagsJson = detail?.tagsJson?.trim() ? detail.tagsJson : "[]";

  const tags: string[] = (() => {
    try {
      const arr = JSON.parse(tagsJson);
      return Array.isArray(arr) ? arr.filter((x) => typeof x === "string") : [];
    } catch {
      return [];
    }
  })();

  const welcomeTitle = detail?.welcomeTitle?.trim() || "";
  const welcomeIntro = detail?.welcomeIntro?.trim() || "";

  const welcomeQuestions: string[] = (() => {
    try {
      const raw = detail?.welcomeQuestionsJson?.trim();
      if (!raw) return [];
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.filter((x) => typeof x === "string") : [];
    } catch {
      return [];
    }
  })();

  const welcomeKeywords: string[] = (() => {
    try {
      const raw = detail?.welcomeKeywordsJson?.trim();
      if (!raw) return [];
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.filter((x) => typeof x === "string") : [];
    } catch {
      return [];
    }
  })();

  return (
    <div className="space-y-4">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div className="text-lg font-bold">
          {isEdit ? "KB Document 수정" : "KB Document 신규 등록"}
        </div>
        <button
          type="button"
          onClick={onCancel}
          disabled={isProcessing}
          className="text-sm px-2 py-1 rounded border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          닫기
        </button>
      </div>

      {/* 진행 상태 카드 (edit + 인덱싱 진행중일 때 유용) */}
      {detail?.id && (
        <div className="rounded border bg-gray-50 p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-semibold text-gray-700">
                인덱싱 진행 상태
              </div>

              {progressStep === 99 ? (
                <div className="mt-1 text-sm text-red-700">
                  처리 실패: {detail.indexError}
                </div>
              ) : progressStep === 3 ? (
                <div className="mt-1 text-sm text-green-700">
                  {progressText || "완료"}
                </div>
              ) : progressStep === 0 ? (
                <div className="mt-1 text-sm text-gray-600">
                  상태 확인 대기 중...
                </div>
              ) : (
                <div className="mt-1 flex items-center gap-2 text-sm text-gray-700">
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
                  <span>{progressText}</span>
                </div>
              )}

              <div className="mt-1 text-[11px] text-gray-500">
                저장 후 분석 작업은 목록에서 자동으로 확인합니다.
              </div>
            </div>

            <div className="flex shrink-0 flex-col gap-2">
              <span className="text-[11px] text-gray-400">
                목록에서 상태 확인
              </span>
            </div>
          </div>

          {/* 결과(요약) 표시 */}
          {progressStep === 3 && summaryText?.trim() && (
            <div className="mt-3">
              <div className="text-xs font-semibold text-gray-700 mb-1">
                AI 요약 결과 (index_summary)
              </div>
              <div className="rounded border bg-white p-3 text-sm whitespace-pre-wrap">
                {summaryText}
              </div>
            </div>
          )}

          {progressStep === 3 && (
          <div className="md:col-span-2">
            <div className="text-xs font-semibold text-gray-700 mb-1">
              AI 웰컴 메타 결과
            </div>

            <div className="rounded border bg-white p-3 space-y-3">
              <div>
                <div className="text-[11px] font-medium text-gray-500 mb-1">welcome_title</div>
                <div className="text-sm text-gray-800">
                  {welcomeTitle || <span className="text-gray-400">생성된 제목이 없습니다.</span>}
                </div>
              </div>

              <div>
                <div className="text-[11px] font-medium text-gray-500 mb-1">welcome_intro</div>
                <div className="text-sm text-gray-800 whitespace-pre-wrap">
                  {welcomeIntro || <span className="text-gray-400">생성된 소개문이 없습니다.</span>}
                </div>
              </div>

              <div>
                <div className="text-[11px] font-medium text-gray-500 mb-1">welcome_questions</div>
                <div className="flex flex-wrap gap-2">
                  {welcomeQuestions.length > 0 ? (
                    welcomeQuestions.map((q, idx) => (
                      <span
                        key={`${idx}-${q}`}
                        className="px-2 py-1 text-xs rounded-full border bg-blue-50 text-blue-700"
                      >
                        {q}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-gray-400">생성된 질문이 없습니다.</span>
                  )}
                </div>
              </div>

              <div>
                <div className="text-[11px] font-medium text-gray-500 mb-1">welcome_keywords</div>
                <div className="flex flex-wrap gap-2">
                  {welcomeKeywords.length > 0 ? (
                    welcomeKeywords.map((k) => (
                      <span
                        key={k}
                        className="px-2 py-1 text-xs rounded-full border bg-emerald-50 text-emerald-700"
                      >
                        #{k}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-gray-400">생성된 키워드가 없습니다.</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      )}

      {/* 폼 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* kbSourceId */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            KB Source ID <span className="text-red-500">*</span>
          </label>
          <input
            name="kbSourceId"
            type="number"
            value={form.kbSourceId || ""}
            onChange={handleChange}
            className="w-full h-10 border rounded px-2 text-sm"
            placeholder="예: 12"
          />
          <p className="mt-1 text-[11px] text-gray-400">
            문서는 특정 KB Source에 귀속됩니다. (일단 ID 입력 방식)
          </p>
        </div>

        {/* useTf */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            사용여부
          </label>
          <select
            name="useTf"
            value={form.useTf ?? "Y"}
            onChange={handleChange}
            className="w-full h-10 border rounded px-2 text-sm bg-white"
          >
            <option value="Y">사용(Y)</option>
            <option value="N">미사용(N)</option>
          </select>
          <p className="mt-1 text-[11px] text-gray-400">
            사용여부는 챗봇/RAG에서 조회 가능 여부에 활용됩니다.
          </p>
        </div>

        {/* title */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            제목 <span className="text-red-500">*</span>
          </label>
          <input
            name="title"
            value={form.title ?? ""}
            onChange={handleChange}
            className="w-full h-10 border rounded px-2 text-sm"
            placeholder="예: 2026학년도 학교장추천 시스템 안내"
          />
        </div>

        {/* docType */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            문서 타입
          </label>
          <select
            name="docType"
            value={form.docType ?? "FILE"}
            onChange={handleChange}
            className="w-full h-10 border rounded px-2 text-sm bg-white"
          >
            <option value="FILE">FILE</option>
            <option value="IMAGE">IMAGE</option>
          </select>
        </div>

        {/* docStatus */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            상태
          </label>
          <select
            name="docStatus"
            value={form.docStatus ?? "DRAFT"}
            onChange={handleChange}
            className="w-full h-10 border rounded px-2 text-sm bg-white"
          >
            <option value="DRAFT">DRAFT</option>
            <option value="READY">READY</option>
            <option value="INDEXED">INDEXED</option>
            <option value="FAILED">FAILED</option>
          </select>
        </div>

        {/* category */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            카테고리
          </label>
          <input
            name="category"
            value={form.category ?? ""}
            onChange={handleChange}
            className="w-full h-10 border rounded px-2 text-sm"
            placeholder="예: 입학/전형/학교장추천"
          />
        </div>

        {/* sourceUrl */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            원본 URL (선택)
          </label>
          <input
            name="sourceUrl"
            value={form.sourceUrl ?? ""}
            onChange={handleChange}
            className="w-full h-10 border rounded px-2 text-sm"
            placeholder="https://..."
          />
        </div>

        {/* 요약 지시문 (선택) - ingest 시 LLM 요약에 사용 */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            요약 지시문 (선택)
          </label>
          <textarea
            name="summaryPrompt"
            value={form.summaryPrompt ?? ""}
            onChange={handleChange}
            rows={4}
            maxLength={2000}
            className="w-full border rounded px-2 py-1.5 text-sm resize-y"
            placeholder="Bullet-summarize five key keywords in this document. For each keyword, provide a brief source textual explanation. Provide the entire response in English, followed by a full Korean translation."
          />
          <p className="mt-1 text-[11px] text-gray-400">
            인덱싱 시 이 문서의 요약을 생성할 때 사용할 LLM 지시문입니다. 비우면 기본 지시문이 사용됩니다. (최대 2000자)
          </p>
        </div>

        {/* 결과(태그) 표시 */}
        {progressStep === 3 && (
          <div className="md:col-span-2">
            <div className="text-xs font-semibold text-gray-700 mb-1">
              AI 태그 결과 (tags_json)
            </div>

            <div className="flex flex-wrap gap-2 rounded border bg-white p-3">
              {tags.length > 0 ? (
                tags.map((t) => (
                  <span key={t} className="px-2 py-1 text-xs rounded-full border bg-gray-50">
                    #{t}
                  </span>
                ))
              ) : (
                <span className="text-xs text-gray-400">생성된 태그가 없습니다.</span>
              )}
            </div>
          </div>
        )}

        {/* 파일 업로드 */}
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            파일 업로드 (선택)
          </label>

          {/* 기존 파일 표시 (수정 모드) */}
          {existingFileInfo && (
            <div className="mb-2 rounded border bg-gray-50 px-3 py-2 text-xs text-gray-700">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium truncate">
                    기존 파일: {existingFileInfo.name}
                  </div>
                  <div className="text-[11px] text-gray-500 truncate">
                    {existingFileInfo.size ? `크기: ${existingFileInfo.size} · ` : ""}
                    {existingFileInfo.path}
                  </div>
                </div>
                <div className="text-[11px] text-gray-500">
                  {file ? "새 파일로 교체 예정" : "유지"}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <input
              type="file"
              onChange={handleFileChange}
              className="block w-full text-sm"
              accept=".pdf,.txt,.md,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.json,.jpg,.jpeg,.png,.gif,.bmp,.webp"
            />

            {/* 선택된 파일 표시 */}
            {file && (
              <div className="flex items-center justify-between rounded border px-3 py-2 text-xs">
                <div className="min-w-0">
                  <div className="font-medium truncate">{file.name}</div>
                  <div className="text-[11px] text-gray-500">
                    {formatBytes(file.size)} · {file.type || "unknown"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveSelectedFile}
                  className="ml-3 px-2 py-1 rounded border hover:bg-gray-50"
                >
                  제거
                </button>
              </div>
            )}

            <p className="text-[11px] text-gray-400">
              저장 시 body(JSON) + file(multipart)로 전송됩니다.
            </p>
          </div>
        </div>
      </div>

      {/* 에러 */}
      {err && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">
          {err}
        </div>
      )}

      {/* 액션 */}
      <div className="flex justify-end gap-2 pt-2 border-t">
        <button
          type="button"
          onClick={onCancel}
          disabled={isProcessing}
          className="h-10 px-4 text-sm rounded border bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          취소
        </button>

        {!lockedAfterDone && (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || saving || isProcessing}
            className="h-10 px-4 text-sm rounded bg-gray-900 text-white hover:bg-black disabled:opacity-50"
          >
            {saving ? "저장 중..." : "저장"}
          </button>
        )}
        {lockedAfterDone && (
          <button
            type="button"
            onClick={onCancel}
            className="h-10 px-4 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700"
          >
            완료 · 닫기
          </button>
        )}
      </div>
    </div>
  );
}
